/**
 * Auth0 Configuration
 * Handles Auth0 configuration based on environment
 * Supports both build-time and runtime configuration
 */

import { loadRuntimeConfig, getCachedConfig } from './runtimeConfig';

export interface Auth0Config {
  domain: string;
  clientId: string;
  audience: string;
}

// Build-time Auth0 configuration (fallback)
const getBuildTimeAuth0Config = (): Auth0Config => {
  return {
    domain: import.meta.env.VITE_AUTH0_DOMAIN || '',
    clientId: import.meta.env.VITE_AUTH0_CLIENT_ID || '',
    audience: import.meta.env.VITE_AUTH0_AUDIENCE || '',
  };
};

/**
 * Gets the Auth0 configuration, preferring runtime config over build-time config
 * @returns Promise<Auth0Config> - The Auth0 configuration
 */
export async function getAuth0Config(): Promise<Auth0Config> {
  try {
    // Try to get runtime config
    const runtimeConfig = await loadRuntimeConfig();
    if (runtimeConfig.auth0) {
      return runtimeConfig.auth0;
    }
  } catch (error) {
    console.warn('Failed to load runtime Auth0 config, using build-time config:', error);
  }

  // Fallback to build-time config
  return getBuildTimeAuth0Config();
}

/**
 * Gets the Auth0 configuration synchronously (uses cached runtime config if available)
 * @returns Auth0Config | null - The Auth0 configuration or null if not loaded yet
 */
export function getAuth0ConfigSync(): Auth0Config | null {
  // Try cached runtime config first
  const cachedConfig = getCachedConfig();
  if (cachedConfig?.auth0) {
    return cachedConfig.auth0;
  }

  // Fallback to build-time config
  const buildTimeConfig = getBuildTimeAuth0Config();
  
  // Only return if we have the required fields
  if (buildTimeConfig.domain && buildTimeConfig.clientId) {
    return buildTimeConfig;
  }

  return null;
}
